import { Tooltip } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { BiChevronDown, BiChevronUp } from "react-icons/bi";
import { formatNumberWithCommas } from "helpers/utility/formatters";
import { PAGE_PARAMETER } from "helpers/constants/miscellaneous";
import ChevronIcon from "assets/icons/ChevronIcon";
import classNames from "classnames";

import styles from "./style.module.scss";

function updatePageParam(navigateFunc: any, searchParams: any, newValue: any) {
  searchParams.set(PAGE_PARAMETER, newValue.toString());
  navigateFunc({ search: `?${searchParams.toString()}` });
}

interface PaginiatorTypes {
  totalCount: number;
  currentBracket: string;
  type: string;
  currentPage: number;
  totalPages: number;
}

interface PageOptionsProps {
  totalPages: number;
  currentPage: number;
  handleClick: (option: number) => void;
}

const PageOptions = ({
  totalPages,
  currentPage,
  handleClick,
}: PageOptionsProps) => {
  const dynamicTopValue = (totalPages > 8 ? 8 : totalPages) * 40 + 5;
  const optionsContainerRef = useRef<HTMLDivElement>(null);

  const scrollToOption = (index: number) => {
    const singleOption = optionsContainerRef.current?.children[index];
    if (singleOption) {
      singleOption.scrollIntoView({ block: "center" });
    }
  };

  useEffect(() => {
    const selectedIndex = currentPage - 1;
    scrollToOption(selectedIndex);
  }, [currentPage]);

  return (
    <div
      className={styles.optionsContainer}
      style={{ top: `-${dynamicTopValue}px` }}
      ref={optionsContainerRef}
    >
      {[...Array(totalPages)].map((_: any, index: number) => {
        const singleOption = index + 1;
        const isSelected = singleOption === currentPage ? styles.selected : "";

        return (
          <div
            key={singleOption}
            className={`${styles.singleOption} ${isSelected}`}
            onClick={() => handleClick(singleOption)}
          >
            {singleOption}
          </div>
        );
      })}
      <div></div>
    </div>
  );
};

const Paginator = ({
  totalCount,
  currentBracket,
  type,
  totalPages,
  currentPage,
}: PaginiatorTypes) => {
  const [showOptions, setShowOptions] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const pageOptionsRef = useRef<HTMLDivElement>(null);

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const typeText = type.toLowerCase();

  const toggleShowOptions = () => {
    setShowOptions(!showOptions);
  };

  const isLeftAvailable = currentPage > 1;
  const isRightAvailable = currentPage < totalPages;

  const prevPageBtnClassnames = classNames(styles.previousPageButton, {
    [styles.disabledIcon]: !isLeftAvailable,
  });

  const nextPageBtnClassnames = classNames(styles.nextPageButton, {
    [styles.disabledIcon]: !isRightAvailable,
  });

  const previousPageClick = async () => {
    if (currentPage === 1) return;
    updatePageParam(navigate, searchParams, currentPage - 1);
  };
  const nextPageClick = () => {
    if (currentPage === totalPages) return;
    updatePageParam(navigate, searchParams, currentPage + 1);
  };
  const handleOptionSelection = (option: number) => {
    if (option !== currentPage) {
      updatePageParam(navigate, searchParams, option);
    }
    setShowOptions(false);
  };

  const handleOutsideClick = (event: any) => {
    if (
      pageOptionsRef.current &&
      !pageOptionsRef.current.contains(event.target)
    ) {
      setShowOptions(false);
    }
  };

  useEffect(() => {
    if (showOptions) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showOptions]);

  return (
    <div className={styles.paginatorBox}>
      <div className={styles.textContainer}>
        <Tooltip title="Previous page">
          <div className={prevPageBtnClassnames} onClick={previousPageClick}>
            <ChevronIcon direction="left" />
          </div>
        </Tooltip>

        <div className={styles.text}>
          <span>{currentBracket} </span>
          of <span>{formatNumberWithCommas(totalCount)} </span>
          <span style={{ textTransform: "capitalize" }}>{typeText}</span>
        </div>

        <Tooltip title="Next page">
          <div className={nextPageBtnClassnames} onClick={nextPageClick}>
            <ChevronIcon direction="right" />
          </div>
        </Tooltip>
      </div>
      <div className={styles.pageSelectionContainer}>
        <span className={styles.pageText}>Page:</span>
        <div
          className={styles.pageSelectionBox}
          onClick={toggleShowOptions}
          ref={pageOptionsRef}
        >
          <span className={styles.selectedOption}>{currentPage}</span>
          <div className={styles.showOptionsIcon} onClick={toggleShowOptions}>
            {showOptions ? (
              <BiChevronUp size="1.60em" />
            ) : (
              <BiChevronDown size="1.60em" />
            )}
          </div>
          {showOptions && (
            <PageOptions
              totalPages={totalPages}
              currentPage={currentPage}
              handleClick={handleOptionSelection}
            />
          )}
        </div>
      </div>
    </div>
  );
};
export default Paginator;
